/**
 * Telemetry module for CTDCommons — the ONLY file that calls PostHog
 * directly. Every event in the commons surface goes through the named
 * functions on the `Telemetry` object below.
 *
 * Rules (docs/skills/telemetry/SKILL.md):
 *   - No screen/page/component imports `posthog-js` — they import
 *     `Telemetry` from here and call named methods.
 *   - Every event is tagged with `source: 'ctdcommons'` so it can be
 *     partitioned from the main-app and add-in surfaces in PostHog.
 *   - No PII in properties — IDs, file_types, and counts only. Never
 *     filenames, emails, document content, or file paths.
 *   - Every catch block also fires `Telemetry.errorSwallowed(...)` so
 *     there are no silent failures.
 *
 * Initialization is env-gated on `VITE_POSTHOG_API_KEY`. If the key is
 * missing (most local dev setups), the module no-ops — every named
 * function becomes a cheap early return, nothing crashes.
 */

import posthog from 'posthog-js'
import type {
  ApiResponseSlowProps,
  CatalogIndClickedProps,
  CreateAccountCtaClickedProps,
  DocumentDiffStartedProps,
  DocumentDiffViewedProps,
  DocumentDownloadCompletedProps,
  DocumentDownloadFailedProps,
  DocumentDownloadStartedProps,
  EmailVerifiedProps,
  ErrorSwallowedProps,
  ExplorerViewedProps,
  LoginHintClickedProps,
  OutboundClickedProps,
  PageViewedProps,
  PasswordResetCompletedProps,
  PasswordResetRequestedProps,
  PortfolioListViewedProps,
  PortfolioSelectedProps,
  SessionEndedProps,
  SessionStartedProps,
  SignInFailedProps,
  SignupStartedProps,
  UserRegisteredProps,
  UserSignedInProps,
  UserSignedOutProps,
  ViewerOpenedProps,
  ViewerRenderFailedProps,
  ViewerSectionRenderedProps,
} from './telemetry-types'

// --- init ------------------------------------------------------------

const POSTHOG_API_KEY =
  (import.meta.env.VITE_POSTHOG_API_KEY as string | undefined) || ''
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  'https://us.i.posthog.com'

let initialized = false

export function initTelemetry(): void {
  if (initialized) return
  if (!POSTHOG_API_KEY) {
    // Expected in local dev — silent.
    return
  }
  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
    })
    initialized = true
    initSession()
    capture('session_started', {})
  } catch {
    // Never block the app on telemetry init.
  }
}

// --- session state (module-level) -----------------------------------

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16)
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

let sessionId = ''
let sessionStartedAt = 0
let currentScreen = ''

// Context synced from React providers via setTelemetryContext()
let contextUserId = 'anonymous'
let contextOrgId = 'none'
let contextPortfolioId = 'none'

function initSession(): void {
  sessionId = generateUUID()
  sessionStartedAt = Date.now()
  currentScreen = ''
}

export function setCurrentScreen(path: string): void {
  currentScreen = path
}

export function setTelemetryContext(ctx: {
  userId?: string
  orgId?: string | null
  portfolioId?: string
}): void {
  if (ctx.userId !== undefined) contextUserId = ctx.userId || 'anonymous'
  if (ctx.orgId !== undefined) contextOrgId = ctx.orgId || 'none'
  if (ctx.portfolioId !== undefined) {
    contextPortfolioId = ctx.portfolioId || 'none'
  }
}

export function getSessionDurationSeconds(): number {
  if (!sessionStartedAt) return 0
  return Math.round((Date.now() - sessionStartedAt) / 1000)
}

// --- common props merged into every event ---------------------------

function getCommonProps(): Record<string, unknown> {
  return {
    source: 'ctdcommons',
    // Partition events by deploy environment in PostHog. Sourced from
    // Vercel's VERCEL_ENV at build time via vite.config.ts. See A2F-785.
    env: __APP_ENV__,
    user_id: contextUserId,
    // org_id lets analysts filter cross-surface per tenant — commons
    // is one tenant alongside every enterprise customer in the same
    // PostHog project. For commons this is near-constant today (the
    // single public-signup org), but firing it on every event means
    // "every Chrona event has org_id" becomes a clean invariant once
    // A2F-447 adds the same prop to the main-app telemetry module.
    org_id: contextOrgId,
    portfolio_id: contextPortfolioId,
    session_id: sessionId,
    screen_name: currentScreen,
  }
}

function capture(eventName: string, props: object = {}): void {
  if (!initialized) return
  try {
    posthog.capture(eventName, {
      ...getCommonProps(),
      ...(props as Record<string, unknown>),
    })
  } catch {
    // Never block features on telemetry send failure.
  }
}

// --- error_message sanitizer ----------------------------------------

/**
 * Sanitize raw error messages before they're sent to PostHog.
 *
 * Callsites pass `err.message` from Supabase / fetch / network errors
 * which can include PII (emails in auth errors, URLs with tokens,
 * filenames in download errors). This is the chokepoint that keeps
 * telemetry-types.ts's "no PII in properties" invariant true regardless
 * of what callsites pass. See A2F-783.
 *
 * Replaces:
 *   - email addresses → [email]
 *   - http/https URLs → [url]
 *   - quoted strings (single, double, backtick) → [quoted]
 *
 * Paths and free file-extension references are intentionally NOT
 * stripped — too many false positives in actual technical error
 * messages (e.g. "POST /api/foo failed"). If a callsite has a path
 * that's PII-sensitive, wrap it in quotes at the source.
 */
export function sanitizeErrorMessage(raw: string): string {
  if (!raw) return raw
  return raw
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
    .replace(/https?:\/\/\S+/g, '[url]')
    .replace(/(["'`])(?:(?!\1).)*\1/g, '[quoted]')
}

function captureError<P extends { error_message: string }>(
  eventName: string,
  props: P,
): void {
  capture(eventName, { ...props, error_message: sanitizeErrorMessage(props.error_message) })
}

// --- Telemetry API — one function per event -------------------------

export const Telemetry = {
  // Identification
  identify: (userId: string, props: Record<string, unknown> = {}) => {
    if (!initialized) return
    try {
      posthog.identify(userId, props)
    } catch {
      /* silent */
    }
  },
  reset: () => {
    if (!initialized) return
    try {
      posthog.reset()
    } catch {
      /* silent */
    }
  },

  // Session
  sessionEnded: (props: SessionEndedProps) => capture('session_ended', props),
  pageViewed: (props: PageViewedProps) => capture('page_viewed', props),

  // Auth
  userSignedIn: (props: UserSignedInProps) => capture('user_signed_in', props),
  userSignedOut: (props: UserSignedOutProps) => capture('user_signed_out', props),
  signInFailed: (props: SignInFailedProps) => capture('sign_in_failed', props),
  signupStarted: (props: SignupStartedProps = {}) =>
    capture('signup_started', props),
  userRegistered: (props: UserRegisteredProps = {}) =>
    capture('user_registered', props),
  emailVerified: (props: EmailVerifiedProps = {}) =>
    capture('email_verified', props),
  passwordResetRequested: (props: PasswordResetRequestedProps = {}) =>
    capture('password_reset_requested', props),
  passwordResetCompleted: (props: PasswordResetCompletedProps = {}) =>
    capture('password_reset_completed', props),
  loginHintClicked: (props: LoginHintClickedProps = {}) =>
    capture('login_hint_clicked', props),

  // Public landing engagement (A2F-732)
  outboundClicked: (props: OutboundClickedProps) =>
    capture('outbound_clicked', props),
  createAccountCtaClicked: (props: CreateAccountCtaClickedProps) =>
    capture('create_account_cta_clicked', props),
  catalogIndClicked: (props: CatalogIndClickedProps) =>
    capture('catalog_ind_clicked', props),

  // Portfolios
  portfolioListViewed: (props: PortfolioListViewedProps) =>
    capture('portfolio_list_viewed', props),
  portfolioSelected: (props: PortfolioSelectedProps) =>
    capture('portfolio_selected', props),

  // Explorer
  explorerViewed: (props: ExplorerViewedProps) =>
    capture('explorer_viewed', props),
  documentDiffStarted: (props: DocumentDiffStartedProps) =>
    capture('document_diff_started', props),
  documentDiffViewed: (props: DocumentDiffViewedProps) =>
    capture('document_diff_viewed', props),

  // Viewer
  viewerOpened: (props: ViewerOpenedProps) => capture('viewer_opened', props),
  viewerSectionRendered: (props: ViewerSectionRenderedProps) =>
    capture('viewer_section_rendered', props),
  viewerRenderFailed: (props: ViewerRenderFailedProps) =>
    captureError('viewer_render_failed', props),

  // Downloads
  documentDownloadStarted: (props: DocumentDownloadStartedProps) =>
    capture('document_download_started', props),
  documentDownloadCompleted: (props: DocumentDownloadCompletedProps) =>
    capture('document_download_completed', props),
  documentDownloadFailed: (props: DocumentDownloadFailedProps) =>
    captureError('document_download_failed', props),

  // Performance
  apiResponseSlow: (props: ApiResponseSlowProps) =>
    capture('api_response_slow', props),

  // Errors
  errorSwallowed: (props: ErrorSwallowedProps) =>
    captureError('error_swallowed', props),
}
